/**
 * Produce a deterministic sort key for a school name.
 *
 * Trims surrounding whitespace, strips leading punctuation/symbols, and
 * falls back to the trimmed name when stripping leaves an empty string so
 * the sort stays deterministic for symbol-only names. This makes names like
 * "@Home" sort under H and "#1 School" sort under 1, instead of clustering
 * at the top of the list in Unicode-codepoint order.
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
function sortKey(name) {
  const trimmed = (name ?? "").trim()
  return trimmed.replace(/^[^\p{L}\p{N}]+/u, "") || trimmed
}

/**
 * Return a new array of schools sorted alphabetically by `name`, using a
 * numeric-aware, case-insensitive collation that ignores leading
 * punctuation/symbols and surrounding whitespace.
 *
 * Does not mutate the input. Returns an empty array for null/undefined
 * input. Schools without a `name` are sorted using an empty key.
 *
 * @template {{ name?: string|null }} T
 * @param {T[]|null|undefined} schools
 * @returns {T[]}
 */
export function sortSchoolsByName(schools) {
  if (!schools) return []
  return [...schools].sort((a, b) =>
    sortKey(a?.name).localeCompare(
      sortKey(b?.name),
      undefined,
      { numeric: true, sensitivity: "base" }
    )
  )
}
